import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { adminPeriodDetailOptions, adminPeriodDraftPreviewOptions } from '../../lib/resources/queries';
import { useFinalizeMaster } from '../../lib/resources/mutations';
import type { AdminPeriodCard, AppliedFee } from '../../lib/resources/types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusPip, statusPipForSubmission } from '../../components/ui/status-pip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { KeyValueRow } from '../../components/ui/key-value-row';
import { formatMoney, monthLabel } from '../../lib/utils';

interface PeriodDetailProps {
  periodId: number;
  card: AdminPeriodCard;
}

/** Expanded body for an admin period row — contractor table + draft preview + finalize CTA. */
export function PeriodDetail({ periodId, card }: PeriodDetailProps) {
  const detail = useQuery(adminPeriodDetailOptions(periodId));
  const preview = useQuery(adminPeriodDraftPreviewOptions(periodId));
  const finalizeMaster = useFinalizeMaster(periodId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (detail.isLoading || !detail.data) {
    return (
      <div className="px-12 py-6 flex items-center gap-2 text-foreground-muted text-[13px]">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  }

  const d = detail.data;
  const isLocked = card.status === 'LOCKED';
  const activeMaster = d.activeMaster;
  const draftCount = preview.data ? d.contractors.length - preview.data.finalizedSubmissionsCount : 0;

  return (
    <div className="px-12 py-6 bg-background/50 border-t border-border space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>
            Contractors
            {isLocked && activeMaster?.generatedInvoiceNumber && (
              <span className="ml-2 font-mono text-[12px] text-foreground-muted">
                · {activeMaster.generatedInvoiceNumber}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-foreground-muted bg-background">
                <th className="text-left px-4 py-2 font-medium">Contractor</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Invoice #</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
                <th className="px-4 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {d.contractors.map((c) => (
                <tr key={c.submissionId} className="border-t border-border">
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.legalName}</div>
                    <div className="text-[11px] text-foreground-muted">{c.email}</div>
                  </td>
                  <td className="px-4 py-2"><StatusPip {...statusPipForSubmission(c.status)} /></td>
                  <td className="px-4 py-2 font-mono text-[12px]">{c.invoiceNumber ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {c.invoiceTotalCents != null ? formatMoney(c.invoiceTotalCents) : '—'}
                  </td>
                  <td className="px-4 py-2">
                    {c.status === 'FINALIZED' && (
                      <Button asChild size="sm" variant="ghost">
                        <a href={`/api/files/contractor-invoice/${c.submissionId}/download`} target="_blank" rel="noreferrer">
                          <Download className="size-3.5" />
                        </a>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {isLocked && activeMaster ? 'Finalized master invoice' : 'Draft master preview'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FeeBreakdownTable
              subtotalCents={
                isLocked && activeMaster
                  ? activeMaster.subtotalCents
                  : (preview.data?.subtotalCents ?? null)
              }
              appliedFees={d.appliedFees}
              grandTotalCents={
                isLocked && activeMaster
                  ? activeMaster.grandTotalCents
                  : (preview.data?.grandTotalCents ?? null)
              }
            />
            {!isLocked && (
              <div className="px-4 py-2 text-[11px] text-foreground-muted italic">Live preview — finalize to lock</div>
            )}
            {isLocked && activeMaster?.finalizedAt && (
              <div className="px-4 py-2 text-[11px] text-foreground-muted">
                Finalized {new Date(activeMaster.finalizedAt).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex flex-col items-end gap-3 justify-end">
          {!isLocked && draftCount > 0 && (
            <div className="text-[12px] text-warning">⚠ {draftCount} contractors still DRAFT</div>
          )}
          {!isLocked && preview.data && (
            <Button size="lg" onClick={() => setConfirmOpen(true)} disabled={finalizeMaster.isPending}>
              Finalize master invoice for {monthLabel(card.year, card.month)} →
            </Button>
          )}
          {isLocked && card.masterInvoiceNumber && (
            <Button asChild variant="secondary" size="lg">
              <a href={`/api/admin/periods/${periodId}/zip`} target="_blank" rel="noreferrer">
                <Download className="size-3.5" /> Download month ZIP
              </a>
            </Button>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize master for {monthLabel(card.year, card.month)}?</DialogTitle>
            <DialogDescription>
              Locks the period and assigns the next master invoice number from the BG sequence.
              {draftCount > 0 &&
                ` ⚠ ${draftCount} contractors still have DRAFT submissions and won't be included.`}
            </DialogDescription>
          </DialogHeader>
          {preview.data && (
            <div className="text-[13px]">
              <KeyValueRow label="Total" value={formatMoney(preview.data.grandTotalCents)} highlight />
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={() =>
                finalizeMaster.mutate(preview.data?.feeConfigEtag ?? null, {
                  onSuccess: () => setConfirmOpen(false),
                })
              }
              disabled={finalizeMaster.isPending}
            >
              {finalizeMaster.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Finalize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface FeeBreakdownTableProps {
  subtotalCents: number | null;
  appliedFees: AppliedFee[];
  grandTotalCents: number | null;
}

function FeeBreakdownTable({ subtotalCents, appliedFees, grandTotalCents }: FeeBreakdownTableProps) {
  if (subtotalCents == null || grandTotalCents == null) {
    return <div className="px-4 py-3 text-[12px] text-foreground-muted italic">No data</div>;
  }
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-[11px] uppercase tracking-wide text-foreground-muted bg-background">
          <th className="text-left px-4 py-2 font-medium">Description</th>
          <th className="text-right px-4 py-2 font-medium w-32">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-t border-border">
          <td className="px-4 py-2 text-foreground-muted">Subtotal</td>
          <td className="px-4 py-2 text-right font-mono">{formatMoney(subtotalCents)}</td>
        </tr>
        {appliedFees.map((f, i) => (
          <tr key={i} className="border-t border-border">
            <td className="px-4 py-2">
              <span className="font-medium">{f.name}</span>
              {f.type === 'PERCENT' && f.rateBasisPoints != null && (
                <span className="ml-2 text-[11px] text-foreground-muted">
                  {(f.rateBasisPoints / 100).toFixed(2)}%
                </span>
              )}
            </td>
            <td className="px-4 py-2 text-right font-mono">{formatMoney(f.amountCents)}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-foreground">
          <td className="px-4 py-2 font-bold">Grand total</td>
          <td className="px-4 py-2 text-right font-mono font-bold">{formatMoney(grandTotalCents)}</td>
        </tr>
      </tbody>
    </table>
  );
}
